class WebhookHandler {
  constructor(licenseCache) {
    this.licenseCache = licenseCache;
    this.eventHandlers = new Map();
    this.setupDefaultHandlers();
  }

  // Setup default event handlers
  setupDefaultHandlers() {
    // Handle repository creation
    this.on('repository.created', (payload) => {
      console.log('Repository created:', payload.repository.full_name);
      // Clear cache for the new repository
      this.licenseCache.clearCache();
    });

    // Handle repository deletion
    this.on('repository.deleted', (payload) => {
      console.log('Repository deleted:', payload.repository.full_name);
      // Remove from cache
      const repoName = payload.repository.name;
      this.licenseCache.cache.delete(repoName);
      this.licenseCache.etags.delete(repoName);
    });

    // Handle push events (potential license file changes)
    this.on('push', (payload) => {
      const repoName = payload.repository.name;
      const filesChanged = payload.commits?.flatMap(commit => 
        [...(commit.added || []), ...(commit.modified || [])]
      ) || [];

      // Check if license-related files were changed
      const licenseFiles = filesChanged.filter(file => 
        file.includes('rho.yaml') || 
        file.includes('LICENSE') || 
        file.includes('license')
      );

      if (licenseFiles.length > 0) {
        console.log(`License files changed in ${repoName}:`, licenseFiles);
        // Invalidate cache for this repository
        this.licenseCache.cache.delete(repoName);
        this.licenseCache.etags.delete(repoName);
      }
    });

    // Handle pull request events
    this.on('pull_request', (payload) => {
      if (payload.action === 'closed' && payload.pull_request.merged) {
        const repoName = payload.repository.name;
        console.log(`PR merged in ${repoName}, checking for license changes`);
        // Invalidate cache as PR might have changed license files
        this.licenseCache.cache.delete(repoName);
        this.licenseCache.etags.delete(repoName);
      }
    });

    // Handle repository updates
    this.on('repository.updated', (payload) => {
      console.log('Repository updated:', payload.repository.full_name);
      // Clear cache as repository metadata might have changed
      this.licenseCache.clearCache();
    });
  }

  // Register event handler
  on(event, handler) {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, []);
    }
    this.eventHandlers.get(event).push(handler);
  }

  // Remove event handler
  off(event, handler) {
    if (this.eventHandlers.has(event)) {
      const handlers = this.eventHandlers.get(event);
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  // Process webhook payload
  async processWebhook(payload, eventType) {
    try {
      console.log(`Processing webhook event: ${eventType}`);
      
      // Get handlers for this event type
      const handlers = this.eventHandlers.get(eventType) || [];
      
      // Execute all handlers
      const promises = handlers.map(handler => {
        try {
          return handler(payload);
        } catch (error) {
          console.error(`Error in webhook handler for ${eventType}:`, error);
          return Promise.resolve();
        }
      });

      await Promise.all(promises);
      
      // Also check for general repository events
      if (eventType.startsWith('repository.')) {
        const generalHandlers = this.eventHandlers.get('repository.*') || [];
        const generalPromises = generalHandlers.map(handler => {
          try {
            return handler(payload, eventType);
          } catch (error) {
            console.error(`Error in general repository handler:`, error);
            return Promise.resolve();
          }
        });
        await Promise.all(generalPromises);
      }

    } catch (error) {
      console.error('Error processing webhook:', error);
      throw error;
    }
  }

  // Validate webhook signature (for security)
  async validateSignature(payload, signature, secret) {
    try {
      // Convert secret to ArrayBuffer
      const encoder = new TextEncoder();
      const keyData = encoder.encode(secret);
      const payloadData = encoder.encode(JSON.stringify(payload));
      
      // Import the secret key
      const key = await crypto.subtle.importKey(
        'raw',
        keyData,
        { name: 'HMAC', hash: 'SHA-256' },
        false,
        ['sign']
      );
      
      // Sign the payload
      const signatureBuffer = await crypto.subtle.sign('HMAC', key, payloadData);
      
      // Convert to hex string
      const signatureArray = new Uint8Array(signatureBuffer);
      const expectedSignature = 'sha256=' + Array.from(signatureArray)
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
      
      // Compare signatures (timing-safe comparison)
      if (signature.length !== expectedSignature.length) {
        return false;
      }
      
      let result = 0;
      for (let i = 0; i < signature.length; i++) {
        result |= signature.charCodeAt(i) ^ expectedSignature.charCodeAt(i);
      }
      
      return result === 0;
    } catch (error) {
      console.error('Error validating webhook signature:', error);
      return false;
    }
  }

  // Get supported event types
  getSupportedEvents() {
    return Array.from(this.eventHandlers.keys());
  }

  // Get event statistics
  getEventStats() {
    const stats = {};
    this.eventHandlers.forEach((handlers, event) => {
      stats[event] = handlers.length;
    });
    return stats;
  }
}

export default WebhookHandler; 